// ui/pages/IpMattersPage.tsx
import React, { useState, useEffect } from "react";
import { DataToolbar } from "../components/DataToolbar";
import { Modal } from "../components/Modal";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { useToast } from "../components/Toast";
import { generateAutoNo } from "../services/counterService";
import { db, serverTimestamp, storage } from "../../firebaseClient";
import { useAuth } from "../context/AuthContext";
import { canViewData } from "../utils/dataAccess";
import { formatDate } from "../utils/dateUtils";
import { SystemLinkagePanel } from "../components/shared/SystemLinkagePanel";
import { AiFieldHeader } from "../components/shared/AiFieldHeader";
import { callLegalAssistant } from "../services/aiService";
import type { AttachmentMeta } from "../types/attachments";
import { uploadIpProtectionFiles } from "../services/storageUpload";

const safeArray = <T,>(v: any): T[] => (Array.isArray(v) ? v : []);

export function IpMattersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const { showToast } = useToast();

  useEffect(() => {
    const q = db.collection("ip_assets").orderBy("createdAt", "desc");
    const unsub = q.onSnapshot((snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const visible = all.filter((item) => canViewData(user as any, item));
      setItems(visible);
      setLoading(false);
    });
    return () => unsub();
  }, [user]);

  const deleteStorageFileIfAny = async (meta?: AttachmentMeta) => {
    const path = meta?.path || (meta as any)?.storagePath;
    if (!path) return;
    try {
      await storage.ref(path).delete();
    } catch (e) {
      // 不阻斷流程：有些歷史檔案可能缺 path / 權限
      console.warn("[ip_assets] delete storage failed:", e);
    }
  };

  const handleDeleteItem = async (item: any) => {
    if (!item?.id) return;
    if (!confirm("確定刪除此筆智財資產？（含附件）")) return;

    try {
      const attachments = safeArray<AttachmentMeta>(item.attachments);
      for (const f of attachments) await deleteStorageFileIfAny(f);

      await db.collection("ip_assets").doc(item.id).delete();
      showToast("已刪除智財資產");
    } catch (e) {
      console.error(e);
      alert("刪除失敗");
    }
  };

  return (
    <div>
      <DataToolbar
        canEdit={true}
        onCreate={() => {
          setEditingItem(null);
          setIsModalOpen(true);
        }}
        onExport={() => {}}
        title="智財資產管理 (IP Assets)"
      />

      <div style={{ display: "grid", gap: 12 }}>
        {loading && (
          <div className="p" style={{ textAlign: "center" }}>
            載入中...
          </div>
        )}

        {items.map((item) => (
          <div
            key={item.id}
            className="tile"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: "#aaa" }}>
                {item.ipNo} | {item.type} | {item.jurisdiction || "台灣"}
              </div>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: 16,
                  color: "#fff",
                  marginTop: 4,
                }}
              >
                {item.title}
              </div>
              {item.status && (
                <div className="badge" style={{ marginTop: 6, fontSize: 10 }}>
                  {item.status}
                </div>
              )}
            </div>

            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: 11,
                  color:
                    item.renewalDate && item.renewalDate < formatDate(new Date())
                      ? "var(--danger)"
                      : "#aaa",
                }}
              >
                續展日：{item.renewalDate || "無"}
              </div>

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                <button
                  className="smallbtn"
                  onClick={() => {
                    setEditingItem(item);
                    setIsModalOpen(true);
                  }}
                >
                  詳情
                </button>

                <button
                  className="smallbtn"
                  style={{ color: "var(--danger)" }}
                  onClick={() => handleDeleteItem(item)}
                >
                  刪除
                </button>
              </div>
            </div>
          </div>
        ))}

        {!loading && items.length === 0 && (
          <div className="p" style={{ textAlign: "center", padding: 40, color: "#666" }}>
            目前無智財資產紀錄
          </div>
        )}
      </div>

      {isModalOpen && (
        <IpAssetForm
          initialData={editingItem}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
}

function IpAssetForm({ initialData, onClose, onSuccess }: any) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [form, setForm] = useState<any>(
    initialData || {
      type: "商標",
      status: "有效",
      jurisdiction: "台灣",
      renewalDate: "",
      department: user?.email ? user.email.split("@")[0].toUpperCase() : "",
      attachments: [],
      relatedNos: {},
    }
  );

  const [rawFiles, setRawFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!initialData && !form.ipNo) {
      generateAutoNo("IP", formatDate(new Date()).replace(/-/g, ""), 3).then((no) =>
        setForm((p: any) => ({ ...p, ipNo: no }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const deleteStorageFileIfAny = async (meta?: AttachmentMeta) => {
    const path = meta?.path || (meta as any)?.storagePath;
    if (!path) return;
    try {
      await storage.ref(path).delete();
    } catch (e) {
      console.warn("[ip_assets] delete storage failed:", e);
    }
  };

  const handleRemoveRemoteFile = async (file: AttachmentMeta, index: number) => {
    const current = safeArray<AttachmentMeta>(form.attachments);
    const next = current.filter((_, i) => i !== index);
    setForm((p: any) => ({ ...p, attachments: next }));

    // 若是既有資料，立即同步 DB + 刪 storage（最小影響：只動 attachments）
    if (initialData?.id) {
      try {
        await deleteStorageFileIfAny(file);
        await db.collection("ip_assets").doc(initialData.id).update({
          attachments: next,
          updatedAt: serverTimestamp(),
        });
        showToast("附件已刪除");
      } catch (e) {
        console.error(e);
        alert("附件刪除失敗（DB/Storage）");
      }
    }
  };

  const handleAiDraft = async () => {
    setAiLoading(true);
    try {
      const instruction = `請根據以下智財資訊（繁中）：
名稱：${form.title}
類型：${form.type}
地區：${form.jurisdiction}
續展日：${form.renewalDate}

請輸出『管理報告版 + 務實法務版』兩段（管理報告版放上方），內容包含：
1) 風險/到期提醒、需決策點、下一步行動（可直接給主管看）
2) 權利重點、證據留存、續展/授權/維權的可執行清單（可直接給執行端）

並以進攻談判角度補強：若要授權/合作或主張權利，建議可談的條件與底線。`;

      const res = await callLegalAssistant({
        mode: "draft",
        contextType: "IP_MATTER",
        userInstruction: instruction,
      });

      setForm((prev: any) => ({ ...prev, description: String(res?.text || "").trim() }));
      showToast("AI 草稿已生成並填入說明欄位");
    } catch (e: any) {
      console.error(e);
      showToast("AI 服務暫時不可用，請稍後再試。");
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title) return alert("請填寫名稱 / 標題");
    setSaving(true);

    try {
      const existingAttachments = safeArray<AttachmentMeta>(form.attachments);

      // 先確保有 docId（新增要先 add 拿到 id）
      let docId: string | undefined = initialData?.id;

      const basePayload = { ...form, updatedAt: serverTimestamp() };

      if (!docId) {
        const docRef = await db.collection("ip_assets").add({
          ...basePayload,
          attachments: existingAttachments,
          createdAt: serverTimestamp(),
        });
        docId = docRef.id;
      } else {
        await db.collection("ip_assets").doc(docId).update({
          ...basePayload,
          attachments: existingAttachments,
        });
      }

      // 有選檔才上傳
      if (rawFiles.length > 0) {
        const uploaded = await uploadIpProtectionFiles(docId!, rawFiles);
        const merged = [...existingAttachments, ...uploaded];

        await db.collection("ip_assets").doc(docId!).update({
          attachments: merged,
          updatedAt: serverTimestamp(),
        });

        setForm((p: any) => ({ ...p, attachments: merged }));
        setRawFiles([]);
      }

      showToast("已儲存");
      onSuccess();
    } catch (e: any) {
      console.error(e);
      alert("儲存/上傳失敗: " + (e?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={initialData ? "編輯智財資產" : "新增智財資產"} onClose={onClose} width={850}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <SystemLinkagePanel
          relatedNos={form.relatedNos}
          linkKey={form.linkKey}
          onChange={({ relatedNos, linkKey }) => setForm({ ...form, relatedNos, linkKey })}
        />

        <div className="grid">
          <div className="field">
            <label className="label">資產編號</label>
            <input className="input" value={form.ipNo} readOnly style={{ background: "#111", color: "#fbbf24" }} />
          </div>

          <div className="field">
            <label className="label">權利類別</label>
            <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option>商標</option>
              <option>專利</option>
              <option>著作權</option>
              <option>營業秘密</option>
              <option>商號</option>
              <option>其他</option>
            </select>
          </div>

          <div className="field" style={{ gridColumn: "1/-1" }}>
            <label className="label">名稱 / 標題 *</label>
            <input
              className="input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="例: SF-Global 商標申請"
            />
          </div>

          <div className="field">
            <label className="label">管轄地區</label>
            <input
              className="input"
              value={form.jurisdiction}
              onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
              placeholder="例: 台灣"
            />
          </div>

          <div className="field">
            <label className="label">續展 / 到期日</label>
            <input
              type="date"
              className="input"
              value={form.renewalDate}
              onChange={(e) => setForm({ ...form, renewalDate: e.target.value })}
            />
          </div>

          <div className="field" style={{ gridColumn: "1/-1" }}>
            <AiFieldHeader label="說明 / 摘要" loading={aiLoading} onClick={handleAiDraft} disabled={!form.title} />
            <textarea
              className="input"
              rows={5}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="點擊上方按鈕可由 AI 自動生成草稿..."
            />
          </div>
        </div>

        <FileUploadKit
          label="證書 / 申請文件附件"
          existingFiles={safeArray<AttachmentMeta>(form.attachments)}
          rawFiles={rawFiles}
          onFilesSelected={(files) => setRawFiles((p) => [...p, ...files])}
          onRemoveRawFile={(idx) => setRawFiles((p) => p.filter((_, i) => i !== idx))}
          onRemoveFile={handleRemoveRemoteFile}
        />
      </div>

      <div className="row" style={{ marginTop: 24, justifyContent: "flex-end", gap: 10 }}>
        <button
          className="btn"
          style={{ width: "auto", background: "transparent", border: "1px solid #555", color: "#ccc" }}
          onClick={onClose}
          disabled={saving}
        >
          取消
        </button>

        <button className="btn" style={{ width: "auto" }} onClick={handleSave} disabled={saving}>
          {saving ? "處理中..." : "儲存資產"}
        </button>
      </div>
    </Modal>
  );
}
